import type { LinkStart } from '@swchat/client-sdk';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { Icon } from '../design';
import { PORTAL_PROTOCOL } from '../lib/client';
import { useClient } from '../lib/hooks';
import { startPortalLogin } from '../lib/pkce';
import { AuthShell } from './AuthShell';

function errorText(e: unknown) {
  return e instanceof Error ? e.message : String(e);
}

export function Login({ error }: { error?: string }) {
  const [busy, setBusy] = useState(false);
  // Nhãn nút theo hệ thống đăng nhập đang dùng (docs/user-guide.md nhắc nguyên văn nhãn 7w_portal).
  const label = PORTAL_PROTOCOL === 'oidc' ? 'Đăng nhập bằng 7wID' : 'Đăng nhập bằng 7w_portal';
  return (
    <AuthShell testId="login-screen">
      <span className="auth-eyebrow">CHÀO MỪNG TRỞ LẠI</span>
      <h2>Đăng nhập vào 7wChat</h2>
      <p className="auth-lead">Chat nội bộ mã hóa đầu cuối. Dùng tài khoản công ty của bạn để tiếp tục.</p>
      {error && (
        <p className="auth-alert" role="alert" data-testid="login-error">
          {error}
        </p>
      )}
      <button
        className="auth-submit"
        data-testid="login-portal"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void startPortalLogin();
        }}
      >
        <span>{busy ? 'Đang chuyển tới trang đăng nhập…' : label}</span>
        <Icon name="chevron" size={18} />
      </button>
      <p className="auth-support">Cần hỗ trợ truy cập? Liên hệ quản trị viên của bạn.</p>
    </AuthShell>
  );
}

export function WaitingForPortal() {
  return (
    <AuthShell>
      <p className="muted">Đang chờ xác thực từ {PORTAL_PROTOCOL === 'oidc' ? '7wID' : '7w_portal'}…</p>
    </AuthShell>
  );
}

/** Thiết bị đầu tiên: tạo danh tính (khóa sinh ngay trên thiết bị). */
export function SetupIdentity(props: {
  busy?: boolean;
  onStart?: () => void;
  onCreated?: (recoveryKey: string) => void;
  onFailed?: () => void;
}) {
  const client = useClient();
  const [error, setError] = useState<string>();
  const busy = !!props.busy;

  return (
    <AuthShell>
      <section className="auth-step">
        <h2>Chào {client.displayName}</h2>
        <p>Đây là thiết bị đầu tiên của bạn. SW Chat sẽ tạo khóa mã hóa ngay trên thiết bị này.</p>
        {error && <p className="error">{error}</p>}
        <button
          className="primary"
          data-testid="create-identity"
          disabled={busy}
          onClick={async () => {
            setError(undefined);
            props.onStart?.();
            try {
              props.onCreated?.(await client.createIdentity());
            } catch (e) {
              setError(errorText(e));
              props.onFailed?.();
            }
          }}
        >
          {busy ? 'Đang tạo khóa…' : 'Tạo khóa mã hóa'}
        </button>
      </section>
    </AuthShell>
  );
}

/** Hiển thị Recovery Key MỘT lần — docs/adr/0004. */
export function RecoveryKeyNotice({ recoveryKey, onDone }: { recoveryKey: string; onDone: () => void }) {
  const [saved, setSaved] = useState(false);
  return (
    <AuthShell>
      <section className="auth-step">
        <h2>Lưu Recovery Key</h2>
        <p>
          Đây là cách <b>duy nhất</b> để khôi phục lịch sử chat khi mất hết thiết bị. Công ty và máy chủ không giữ bản sao.
          Hãy lưu vào trình quản lý mật khẩu hoặc in ra và cất giữ an toàn.
        </p>
        <code className="recovery-key" data-testid="recovery-key">
          {recoveryKey}
        </code>
        <div className="row">
          <button onClick={() => void navigator.clipboard.writeText(recoveryKey)}>Sao chép</button>
        </div>
        <label className="check">
          <input type="checkbox" data-testid="recovery-saved" checked={saved} onChange={(e) => setSaved(e.target.checked)} /> Tôi đã
          lưu Recovery Key
        </label>
        <button className="primary" data-testid="recovery-done" disabled={!saved} onClick={onDone}>
          Bắt đầu chat
        </button>
      </section>
    </AuthShell>
  );
}

/** Thiết bị mới của người đã có danh tính: khôi phục bằng Recovery Key hoặc liên kết từ thiết bị cũ. */
export function Recovery() {
  const client = useClient();
  const [mode, setMode] = useState<'key' | 'link'>('key');
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [link, setLink] = useState<LinkStart & { qrImage: string }>();

  useEffect(() => {
    if (mode !== 'link' || link) return;
    let cancelled = false;
    (async () => {
      try {
        const start = await client.beginLink();
        const qrImage = await QRCode.toDataURL(start.qr, { margin: 1, width: 220 });
        if (cancelled) return;
        setLink({ ...start, qrImage });
        await client.waitForLink(start.linkId);
      } catch (e) {
        if (!cancelled) setError(errorText(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, mode, link]);

  return (
    <AuthShell>
      <section className="auth-step">
        <h2>Thiết bị mới</h2>
        <p className="muted">
          Tài khoản {client.displayName} đã có khóa mã hóa. Để đọc được tin nhắn trên thiết bị này, hãy chứng minh bạn giữ khóa.
        </p>
        <div className="tabs">
          <button className={mode === 'key' ? 'active' : ''} onClick={() => setMode('key')}>
            Recovery Key
          </button>
          <button className={mode === 'link' ? 'active' : ''} onClick={() => setMode('link')}>
            Từ thiết bị khác
          </button>
        </div>
        {error && <p className="error">{error}</p>}

        {mode === 'key' ? (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              setError(undefined);
              try {
                await client.restoreWithRecoveryKey(key);
              } catch {
                setError('Recovery Key không đúng.');
              } finally {
                setBusy(false);
              }
            }}
          >
            <textarea
              className="mono"
              data-testid="recovery-input"
              rows={3}
              placeholder="XXXX-XXXX-XXXX-…"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
            <button className="primary" data-testid="recovery-restore" disabled={busy || key.replace(/[^A-Za-z0-9]/g, '').length < 52}>
              {busy ? 'Đang khôi phục…' : 'Khôi phục'}
            </button>
          </form>
        ) : (
          <div className="link-box">
            {link ? (
              <>
                <img src={link.qrImage} alt="Mã QR liên kết thiết bị" width={220} height={220} />
                <p>
                  Trên thiết bị đang dùng, mở <b>Thiết bị → Liên kết thiết bị mới</b>, rồi quét mã hoặc dán đoạn mã dưới đây.
                </p>
                <code className="link-payload">{link.qr}</code>
                <button onClick={() => void navigator.clipboard.writeText(link.qr)}>Sao chép mã</button>
                <p>
                  Mã đối chiếu: <b className="mono">{link.code}</b>
                </p>
                <p className="muted">Đang chờ phê duyệt…</p>
              </>
            ) : (
              <p className="muted">Đang tạo mã liên kết…</p>
            )}
          </div>
        )}
      </section>
    </AuthShell>
  );
}

export function Revoked() {
  return (
    <AuthShell>
      <section className="auth-step">
        <h2>Thiết bị đã bị thu hồi</h2>
        <p>Thiết bị này đã bị gỡ khỏi tài khoản và dữ liệu cục bộ đã được xóa.</p>
        <button className="primary" onClick={() => window.location.assign('/')}>
          Đăng nhập lại
        </button>
      </section>
    </AuthShell>
  );
}
